import { Col, Form, Input, Row, Select, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { customFleets } from 'shared';
import { AppStore, ShopStore, UserStore, WorkOrderStore } from 'stores';
import { Logger } from 'stores/Classes';
import {
  WorkOrderRepairPriorityEnum,
  WorkOrderStatusEnum,
  WorkOrderTypeOfServiceEnum,
} from 'stores/Classes/WorkOrderObject';
import CurrentUserStore from 'stores/CurrentUserStore';

const { Option } = Select;
const { Text } = Typography;

const fullWidth = {
  width: '100%',
};

const randomWorkOrderNumber = () => {
  // generate a random number between 1 and 1000000
  // and pad it with 0s to make it 6 digits long
  const randomNumber = `000000${Math.floor(Math.random() * 1000000)}`;
  return randomNumber.substring(randomNumber.length - 6);
};

const WorkOrderTMTForm = ({ childRef, onSave, defects, tmtRepairShop }) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [componentCodes, setComponentCodes] = useState([]);
  const [repairReasons, setRepairReasons] = useState([]);

  useEffect(() => {
    if (childRef) {
      childRef({ form });
    }
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadShopUsers(),
        loadComponentCodes(),
        loadComplaintCodes(),
        loadRepairCodes(),
      ]);
    } catch (error) {
      // if AbortError, do nothing
      if (error.name === 'AbortError') {
        return;
      }
      Logger.error('Error loading initial data:', error);
      AppStore.addError(
        'We had an error loading the Form Data. Try again later.'
      );
    }
  };

  const loadShopUsers = async () => {
    const fetchedUsers = await UserStore.fetchUsersForTable({
      offset: 0,
      limit: 100,
      filter: null,
    });
    setUsers(fetchedUsers);
  };

  const loadComponentCodes = async () => {
    const codes = await WorkOrderStore.getTMTComponentCodes();
    setComponentCodes(codes);
  };

  const loadComplaintCodes = async () => {
    const codes = await WorkOrderStore.getTMTComplaintCodes();
    setComplaints(codes);
  };

  const loadRepairCodes = async () => {
    const codes = await WorkOrderStore.getTMTRepairCodes();
    setRepairReasons(codes);
  };

  const getDefectComponentCode = (defect) => {
    const initialCompCode = componentCodes.find(
      (comp) => comp.defect === defect
    );
    return initialCompCode ? initialCompCode.code : undefined;
  };

  const filterOption = (input, option) => {
    return option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const handleSave = () => {
    if (onSave) {
      onSave(true);
    }
  };

  const renderOptions = (items, valueKey = 'key', labelKey = 'value') => {
    return items.map((item, index) => (
      <Option key={item[valueKey] || index} value={item[valueKey]}>
        {item[labelKey]}
      </Option>
    ));
  };

  const renderUserOptions = () =>
    users.map((user, index) => (
      <Option key={index} value={user.userId}>
        {`${user.firstName} ${user.lastName}`.trim() || user.email}
      </Option>
    ));

  const renderComponentCodeOptions = () =>
    componentCodes.map((compCode, index) => (
      <Option key={index} value={compCode.code}>
        {compCode.code
          ? `${compCode.code}: ${compCode.description}`
          : 'No Code'}
      </Option>
    ));

  const renderComplaintOptions = () =>
    complaints.map((complaint, index) => (
      <Option key={index} value={complaint.code}>
        {complaint.code || 'Break'}
      </Option>
    ));

  const renderRepairReasonOptions = () =>
    repairReasons.map((repReason, index) => (
      <Option key={index} value={repReason.code}>
        {repReason.code || 'No Repair Reason'}
      </Option>
    ));

  const statusOptions = Object.keys(WorkOrderStatusEnum).map((key) => ({
    key,
    value: WorkOrderStatusEnum[key],
  }));

  const typeOptions = Object.keys(WorkOrderTypeOfServiceEnum).map((key) => ({
    key,
    value: WorkOrderTypeOfServiceEnum[key],
  }));

  const priorityOptions = Object.keys(WorkOrderRepairPriorityEnum).map(
    (key) => ({
      key,
      value: WorkOrderRepairPriorityEnum[key],
    })
  );

  return (
    <Form
      form={form}
      onFinish={handleSave}
      initialValues={{
        woInvoiceNumber: randomWorkOrderNumber(),
        woCreatedBy: CurrentUserStore.user.id,
        woAssignedTo: CurrentUserStore.user.id,
        woStatus: 'open',
        woRepairType: 'driver_identified',
        woRepairPriority: 'major',
      }}
      layout="vertical"
    >
      <Row style={{ marginTop: 15 }} gutter={24}>
        <Col span={12}>
          <Text strong>Work Order Details</Text>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          {customFleets.summitMaterialsAll.includes(
            ShopStore.currentShop.id
          ) && (
            <div>
              <Text strong>TMT Repair Shop: </Text>
              <Text>{tmtRepairShop}</Text>
            </div>
          )}
        </Col>
      </Row>

      <Row style={{ marginTop: 15 }} gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Work order number:"
            name="woInvoiceNumber"
            rules={[
              { required: true, message: 'Work order number is required!' },
            ]}
          >
            <Input prefix="#" allowClear placeholder="#000000" />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            label="Created by:"
            name="woCreatedBy"
            rules={[{ required: true, message: 'Created by is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a User"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {renderUserOptions()}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            label="Assigned To:"
            name="woAssignedTo"
            rules={[{ required: true, message: 'Assigned to is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a User"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {renderUserOptions()}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            label="Status:"
            name="woStatus"
            rules={[{ required: true, message: 'Status is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a Status"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {renderOptions(statusOptions)}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            label="Repair Type:"
            name="woRepairType"
            rules={[{ required: true, message: 'Repair Type is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a Repair Type"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
              disabled={true}
            >
              {renderOptions(typeOptions)}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            label="Repair Priority:"
            name="woRepairPriority"
            rules={[
              { required: true, message: 'Repair Priority is required!' },
            ]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a Repair Priority"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {renderOptions(priorityOptions)}
            </Select>
          </Form.Item>
        </Col>

        <Col span={24}>
          <Text strong style={{ marginBottom: 10, display: 'block' }}>
            Defects that will be added to this Work Order
          </Text>
          {defects.map((issue, index) => (
            <Text key={issue.id}>
              <Text underline>
                {issue.defectType}: {issue.comment}
              </Text>
              <Row style={{ marginTop: 15 }} gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Component Code:"
                    name={`componentCode_${index}`}
                    initialValue={getDefectComponentCode(issue.defectType)}
                    rules={[
                      {
                        required: true,
                        message: `Component Code number for ${issue.defectType} is required!`,
                      },
                    ]}
                  >
                    <Select
                      style={fullWidth}
                      placeholder="No Matching Value Found"
                      showSearch
                      optionFilterProp="children"
                      filterOption={filterOption}
                    >
                      {renderComponentCodeOptions()}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    label="Complaint:"
                    name={`complaint_${index}`}
                    initialValue="BROKEN"
                    rules={[
                      { required: true, message: 'Complaint is required!' },
                    ]}
                  >
                    <Select
                      style={fullWidth}
                      placeholder="Select a Component Code"
                      showSearch
                      optionFilterProp="children"
                      filterOption={filterOption}
                    >
                      {renderComplaintOptions()}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    label="Repair Reason:"
                    name={`repairReason_${index}`}
                    initialValue="DVIR"
                    rules={[
                      { required: true, message: 'Repair Reason is required!' },
                    ]}
                  >
                    <Select
                      style={fullWidth}
                      placeholder="Select a Repair Reason"
                      showSearch
                      optionFilterProp="children"
                      filterOption={filterOption}
                    >
                      {renderRepairReasonOptions()}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Text>
          ))}
        </Col>
      </Row>
    </Form>
  );
};

WorkOrderTMTForm.propTypes = {
  childRef: PropTypes.func,
  onSave: PropTypes.func,
  defects: PropTypes.array,
  tmtRepairShop: PropTypes.string,
};

WorkOrderTMTForm.defaultProps = {
  defects: [],
};

export default WorkOrderTMTForm;
